import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X, Image as ImageIcon, AlertCircle } from 'lucide-react';
import { message } from 'antd';

const ModernImageUpload = ({ 
    onUpload, 
    onRemove, 
    currentImage, 
    currentImageVariants,
    loading = false,
    accept = {
        'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    maxSize = 5 * 1024 * 1024, // 5MB
    className = '',
    disabled = false
}) => {
    const [preview, setPreview] = useState(null);
    const [uploading, setUploading] = useState(false);

    const onDrop = useCallback(async (acceptedFiles) => {
        const file = acceptedFiles[0];
        if (!file) return;

        // Create preview
        const reader = new FileReader();
        reader.onload = () => {
            setPreview(reader.result);
        };
        reader.readAsDataURL(file);

        // Upload file
        setUploading(true);
        try {
            const result = await onUpload(file);
            message.success('Image uploaded successfully!');
            setPreview(null);
        } catch (error) {
            console.error('Upload error:', error);
            message.error('Failed to upload image');
            setPreview(null);
        } finally {
            setUploading(false);
        }
    }, [onUpload]);

    const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
        onDrop,
        accept,
        maxSize,
        multiple: false,
        disabled: disabled || loading || uploading
    });

    const handleRemove = () => {
        setPreview(null);
        if (onRemove) {
            onRemove();
        }
    };

    const getImageUrl = () => {
        if (preview) return preview;
        if (currentImageVariants?.medium) return currentImageVariants.medium;
        if (currentImage) return currentImage;
        return null;
    };

    const fileRejectionErrors = fileRejections.map(({ file, errors }) => 
        errors.map(error => `${file.name}: ${error.message}`)
    ).flat();

    return (
        <div className={`modern-image-upload ${className}`}>
            <div
                {...getRootProps()}
                className={`
                    relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
                    transition-all duration-200 hover:border-blue-400 hover:bg-blue-50
                    ${isDragActive ? 'border-blue-500 bg-blue-100' : 'border-gray-300'}
                    ${disabled || loading || uploading ? 'opacity-50 cursor-not-allowed' : ''}
                `}
            >
                <input {...getInputProps()} />
                
                {getImageUrl() ? (
                    <div className="relative">
                        <img
                            src={getImageUrl()}
                            alt="Preview"
                            className="max-w-full max-h-48 mx-auto rounded-lg shadow-md"
                        />
                        {!disabled && !loading && !uploading && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemove();
                                }}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                            >
                                <X size={16} />
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="flex justify-center">
                            {uploading ? (
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                            ) : (
                                <div className="p-3 bg-gray-100 rounded-full">
                                    <ImageIcon size={24} className="text-gray-400" />
                                </div>
                            )}
                        </div>
                        
                        <div>
                            <p className="text-lg font-medium text-gray-700">
                                {isDragActive ? 'Drop the image here' : 'Upload an image'}
                            </p>
                            <p className="text-sm text-gray-500 mt-1">
                                Drag & drop or click to select
                            </p>
                            <p className="text-xs text-gray-400 mt-2">
                                Supports: JPEG, PNG, GIF, WebP (max 5MB)
                            </p>
                        </div>
                        
                        {!isDragActive && (
                            <div className="flex justify-center">
                                <Upload size={20} className="text-gray-400" />
                            </div>
                        )}
                    </div>
                )}
            </div>

            {fileRejectionErrors.length > 0 && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start">
                        <AlertCircle size={16} className="text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                        <div>
                            <p className="text-sm font-medium text-red-800">Upload Error</p>
                            <ul className="text-sm text-red-700 mt-1">
                                {fileRejectionErrors.map((error, index) => (
                                    <li key={index}>{error}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            )}

            {(loading || uploading) && (
                <div className="mt-3 text-center">
                    <p className="text-sm text-blue-600">
                        {uploading ? 'Uploading...' : 'Processing...'}
                    </p>
                </div>
            )}
        </div>
    );
};

export default ModernImageUpload;
